/**
 * LocationTransferDialog.jsx — bin-to-bin inventory transfer.
 *
 * Flow:
 *  1. User picks a From location (typeahead on location code).
 *  2. We GET /api/inventory?location_id=X to load everything currently at
 *     that bin. If the bin holds multiple variants, a radio list appears.
 *  3. User picks the target variant (skipped automatically when there's
 *     exactly one), enters a qty ≤ available, picks a To location.
 *  4. Submit → POST /api/inventory/transfer. The backend validates tenant
 *     scoping + insufficient-stock and logs a movement row automatically.
 *
 * Pure frontend wiring — the backend endpoint has existed since the core
 * inventory router was built. We just didn't have a UI surface for it.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  MagnifyingGlass, ArrowRight, Package, Check, X, ArrowsLeftRight,
} from '@phosphor-icons/react';
import { getLocations, getInventoryAtLocation, transferInventory } from '../lib/api';

// Simple in-dialog location typeahead. Could live as a shared component
// eventually, but for now the extra file isn't worth it.
function LocationPicker({ locations, value, onChange, testIdPrefix, placeholder }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = value ? locations.find((l) => l.id === value) : null;
  // When a value is preselected (e.g. from a "Transfer from this bin" icon
  // on the Locations page) the dialog may render before `locations` has
  // finished loading. In that window, `selected` is null but we still want
  // to show a chip so the operator sees what's pre-selected. We fall back
  // to a minimal "loading…" chip that will swap to the full label once the
  // locations list arrives.
  const hasPreselectedButUnknown = value && !selected;
  const matches = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return [];
    return locations
      .filter((l) => (l.location_code || '').toLowerCase().includes(t) || (l.zone || '').toLowerCase().includes(t))
      .slice(0, 12);
  }, [term, locations]);

  if ((selected || hasPreselectedButUnknown) && !open) {
    return (
      <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-chip`}>
        <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 border border-border rounded bg-muted/40">
          <Check size={14} weight="bold" className="text-emerald-600 shrink-0" />
          <div className="font-mono text-sm font-bold truncate flex-1">
            {selected?.location_code || (
              <span className="text-muted-foreground italic">Loading…</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(true); requestAnimationFrame(() => inputRef.current?.focus()); }}
            data-testid={`${testIdPrefix}-change`}
            className="text-xs font-bold uppercase tracking-wider text-brand hover:underline shrink-0"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="search"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-input`}
          className="pl-9 h-9 font-mono"
          autoComplete="off"
        />
      </div>
      {open && term && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 bg-card border border-border rounded-md shadow-md max-h-[240px] overflow-y-auto"
          data-testid={`${testIdPrefix}-results`}
        >
          {matches.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">No locations match "{term}"</div>
          ) : (
            <ul className="divide-y divide-border">
              {matches.map((l) => (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => { onChange(l.id); setTerm(''); setOpen(false); }}
                    data-testid={`${testIdPrefix}-result-${l.location_code}`}
                    className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2"
                  >
                    <div className="font-mono text-sm font-bold flex-1">{l.location_code}</div>
                    <span className="text-[10px] text-muted-foreground">{l.zone}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function LocationTransferDialog({
  trigger,
  onTransferred,
  // Optional controlled mode — when the parent passes these, the component
  // stops managing its own open/close state. Used by the Locations page to
  // deep-link "Transfer from this bin" buttons into a single shared dialog
  // instance with the From location pre-selected.
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  preselectFromLocationId,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) controlledOnOpenChange?.(v);
    else setInternalOpen(v);
  };

  const [locations, setLocations] = useState([]);

  const [fromLoc, setFromLoc] = useState('');
  const [toLoc, setToLoc] = useState('');
  const [items, setItems] = useState([]); // hydrated inventory rows at fromLoc
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState('');
  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load locations once when the dialog opens so the From/To pickers are
  // ready immediately. Items are fetched separately below keyed on fromLoc.
  useEffect(() => {
    if (!open) return;
    getLocations({ is_active: true })
      .then((res) => setLocations(res.data || []))
      .catch(() => toast.error('Failed to load locations'));
  }, [open]);

  // Whenever the From location changes, fetch the hydrated inventory rows
  // (/api/inventory/at-location/{id} returns sku_code + name + variant
  // attrs inline so the FE doesn't need a second round-trip).
  useEffect(() => {
    if (!fromLoc) { setItems([]); setSelectedVariant(''); setQty(''); return; }
    setItemsLoading(true);
    getInventoryAtLocation(fromLoc)
      .then((res) => {
        const rows = res.data || [];
        setItems(rows);
        if (rows.length === 1) {
          setSelectedVariant(rows[0].variant_id);
          setQty(String(rows[0].quantity));
        } else {
          setSelectedVariant('');
          setQty('');
        }
      })
      .catch(() => { setItems([]); toast.error('Failed to load items at source'); })
      .finally(() => setItemsLoading(false));
  }, [fromLoc]);

  const selectedItem = items.find((i) => i.variant_id === selectedVariant);
  const maxQty = selectedItem?.quantity || 0;

  // When the dialog opens in controlled mode with a preselect, seed fromLoc.
  // We do this in a separate effect keyed on `open` + `preselectFromLocationId`
  // so reopening with a different bin re-seeds correctly.
  useEffect(() => {
    if (!open) return;
    if (preselectFromLocationId && preselectFromLocationId !== fromLoc) {
      setFromLoc(preselectFromLocationId);
    }
    // We intentionally only react to the open/preselect change — otherwise
    // typing a new From would re-seed back to the preselection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselectFromLocationId]);

  const reset = () => {
    setFromLoc(''); setToLoc(''); setItems([]);
    setSelectedVariant(''); setQty('');
  };

  const submit = async (e) => {
    e?.preventDefault();
    const n = parseInt(qty, 10);
    if (!fromLoc || !toLoc) { toast.error('Pick both From and To locations'); return; }
    if (fromLoc === toLoc) { toast.error('Source and destination must differ'); return; }
    if (!selectedVariant) { toast.error('Pick an item to transfer'); return; }
    if (!Number.isFinite(n) || n <= 0) { toast.error('Quantity must be > 0'); return; }
    if (n > maxQty) { toast.error(`Only ${maxQty} available at source`); return; }

    setSubmitting(true);
    try {
      await transferInventory({
        from_location_id: fromLoc,
        to_location_id: toLoc,
        variant_id: selectedVariant,
        quantity: n,
      });
      toast.success(`Moved ${n} unit${n === 1 ? '' : 's'}`, {
        description: `${locations.find(l => l.id === fromLoc)?.location_code} → ${locations.find(l => l.id === toLoc)?.location_code}`,
      });
      reset();
      setOpen(false);
      onTransferred?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" data-testid="open-location-transfer-btn">
              <ArrowsLeftRight size={16} weight="bold" className="mr-2" /> Transfer Stock
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="border border-border rounded-none max-w-lg" data-testid="location-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer Stock Between Locations</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Pick a source bin, choose the item, then pick a destination and quantity.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">From Location</Label>
            <div className="mt-1">
              <LocationPicker
                locations={locations}
                value={fromLoc}
                onChange={setFromLoc}
                testIdPrefix="xfer-from"
                placeholder="Type bin code, e.g. A-01-02-03-04"
              />
            </div>
          </div>

          {fromLoc && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="overline">Item</Label>
                <span className="text-[11px] text-muted-foreground">
                  {itemsLoading ? 'Loading…' : items.length === 0 ? 'empty bin' :
                    items.length === 1 ? '1 item' : `${items.length} items — pick one`}
                </span>
              </div>
              {itemsLoading ? (
                <div className="border border-border rounded p-3 text-sm text-muted-foreground text-center">Loading…</div>
              ) : items.length === 0 ? (
                <div className="border border-rose-200 bg-rose-50 rounded p-3 text-sm text-rose-800 text-center">
                  <Package size={18} weight="duotone" className="inline mr-1" /> No available inventory at this bin.
                </div>
              ) : (
                <div className="border border-border rounded max-h-[220px] overflow-y-auto" data-testid="xfer-items-list">
                  {items.map((it) => {
                    const code = it.sku_code || '(unknown SKU)';
                    const attrs = [it.size, it.color, it.style].filter(Boolean).join(' · ');
                    const active = selectedVariant === it.variant_id;
                    const isOrphan = !it.sku_code;
                    return (
                      <button
                        key={it.inventory_id || it.id || it.variant_id}
                        type="button"
                        onClick={() => { setSelectedVariant(it.variant_id); setQty(String(it.quantity)); }}
                        data-testid={`xfer-item-${it.sku_code || it.variant_id}`}
                        className={`w-full text-left px-3 py-2 border-b last:border-b-0 border-border flex items-center gap-3 transition ${
                          active ? 'bg-brand/10 border-l-4 border-l-brand' : 'hover:bg-muted'
                        }`}
                      >
                        <div className={`h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                          active ? 'bg-brand border-brand text-white' : 'border-muted-foreground/40'
                        }`}>
                          {active && <Check size={10} weight="bold" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`font-mono text-sm font-bold truncate ${isOrphan ? 'text-amber-700' : ''}`}>{code}</div>
                          {(it.sku_name || attrs || isOrphan) && (
                            <div className="text-[11px] text-muted-foreground truncate">
                              {isOrphan
                                ? `orphan row · variant ${String(it.variant_id).slice(-8)}`
                                : <>{it.sku_name}{it.sku_name && attrs ? ' · ' : ''}{attrs}</>}
                            </div>
                          )}
                        </div>
                        <div className="font-mono text-right shrink-0">
                          <div className="text-sm font-bold">{it.quantity}</div>
                          <div className="text-[10px] text-muted-foreground">available</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selectedVariant && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    max={maxQty}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    data-testid="xfer-qty-input"
                    className="mt-1 font-mono"
                  />
                  <div className="flex gap-1 mt-1.5">
                    <button
                      type="button"
                      onClick={() => setQty(String(Math.floor(maxQty / 2)))}
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-border hover:bg-muted rounded"
                    >
                      Half
                    </button>
                    <button
                      type="button"
                      onClick={() => setQty(String(maxQty))}
                      data-testid="xfer-qty-all"
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-border hover:bg-muted rounded"
                    >
                      All ({maxQty})
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <Label className="overline flex items-center gap-2">
                  To Location
                  <ArrowRight size={12} className="text-brand" />
                </Label>
                <div className="mt-1">
                  <LocationPicker
                    locations={locations.filter((l) => l.id !== fromLoc)}
                    value={toLoc}
                    onChange={setToLoc}
                    testIdPrefix="xfer-to"
                    placeholder="Type destination bin code"
                  />
                </div>
              </div>
            </>
          )}

          <Button
            type="submit"
            disabled={
              submitting || !fromLoc || !toLoc || !selectedVariant || !qty ||
              // Mirror the submit-handler guards on the disabled state so the
              // button affordance matches the validation outcome. Without
              // this the user can click with qty>maxQty and only see a toast.
              !Number.isFinite(parseInt(qty, 10)) || parseInt(qty, 10) <= 0 ||
              parseInt(qty, 10) > maxQty
            }
            data-testid="xfer-submit-btn"
            className="w-full bg-brand hover:bg-brand/90 rounded-none"
          >
            {submitting ? 'Moving…' : (
              <>
                <ArrowsLeftRight size={14} weight="bold" className="mr-2" />
                Transfer {qty || 0} unit{qty === '1' ? '' : 's'}
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
